// ===================================================================
// 🧭 COMPONENTE DE NAVEGAÇÃO
// ===================================================================

import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { styles } from '../../styles/globalStyles';
import { useAdminProcess } from '../../contexts/AdminProcessContext';
import { isOrdemInscricaoMode, isProvaMode } from '../../utils/selectionMode';

/** Páginas globais: não dependem do processo seletivo nem da modalidade. */
export const GLOBAL_MENU_PAGE_IDS = ['about'];

export const Navigation = React.memo(({ currentPage, setCurrentPage, userRole }) => {
  const [configMenuOpen, setConfigMenuOpen] = useState(false);
  const { processes, selectedProcessId, isLoadingProcesses } = useAdminProcess();

  const selectedProcess = useMemo(() => {
    if (isLoadingProcesses || !['admin', 'analyst'].includes(userRole || '')) return null;
    return processes.find((p) => String(p.id) === String(selectedProcessId)) || null;
  }, [isLoadingProcesses, processes, selectedProcessId, userRole]);

  const isProvaProcess = Boolean(selectedProcess && isProvaMode(selectedProcess.selection_mode));
  const isOrdemInscricaoProcess = Boolean(selectedProcess && isOrdemInscricaoMode(selectedProcess.selection_mode));
  const isProvaWithAnswerSheet = isProvaProcess && Boolean(selectedProcess?.exam_uses_answer_sheet);
  const isProvaWithEssay = isProvaProcess && Boolean(selectedProcess?.exam_uses_essay);
  const processHasFee = Boolean(selectedProcess?.has_fee);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    setConfigMenuOpen(false);
  }, [setCurrentPage]);

  const handleConfigToggle = useCallback(() => {
    setConfigMenuOpen(prev => !prev);
  }, []);

  useEffect(() => {
    if (!configMenuOpen) return;
    const closeOnClickOutside = () => setConfigMenuOpen(false);
    const t = setTimeout(() => document.addEventListener('click', closeOnClickOutside), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('click', closeOnClickOutside);
    };
  }, [configMenuOpen]);

  const contextNavLinks = useMemo(() => {
    const links = [
      { id: 'institutions', label: 'Instituições', icon: '🏛️', roles: ['master'] },
      { id: 'config', label: 'Configurações', icon: '⚙️', roles: ['master'] },
      { id: 'dashboard', label: 'Minhas inscrições', icon: '📋', roles: ['candidate'] },
      { id: 'painel', label: 'Painel de Controle', icon: '📊', roles: ['admin', 'analyst', 'campus'] },
      { id: 'edital-challenges', label: 'Impugnações', icon: '📢', roles: ['admin'] },
      { id: 'payments', label: 'Pagamentos', icon: '💰', roles: ['admin'], onlyWithFee: true },
      { id: 'analysis', label: isOrdemInscricaoProcess ? 'Inscritos' : 'Análise', icon: '📊', roles: ['admin'], hideForProva: true },
      { id: 'admin-special-attendance', label: 'Atendimento especial', icon: '♿', roles: ['admin'], onlyProva: true },
      { id: 'exam-logistics', label: 'Inscritos', icon: '📋', roles: ['admin'], onlyProva: true },
      { id: 'exam-objective', label: 'Prova objetiva', icon: '📄', roles: ['admin'], onlyProvaWithAnswerSheet: true },
      { id: 'exam-essay', label: 'Redação', icon: '✍️', roles: ['admin'], onlyProvaWithEssay: true },
      { id: 'appeals', label: 'Recursos', icon: '📝', roles: ['admin'] },
      { id: 'reports', label: 'Relatórios', icon: '📄', roles: ['admin', 'campus'] },
      {
        id: 'config',
        label: 'Configuração',
        icon: '⚙️',
        roles: ['admin'],
        submenu: [
          { id: 'processes', label: 'Processos', icon: '📋' },
          { id: 'courses', label: 'Cursos', icon: '🎓' },
          { id: 'course-types', label: 'Tipos de curso', icon: '📚' },
          { id: 'quotas', label: 'Cotas', icon: '🏷️' },
          { id: 'payment_methods', label: 'Métodos de pagamento', icon: '💳' },
          { id: 'questionnaires', label: 'Questionários Socioeconômicos', icon: '📋' },
          { id: 'reasons', label: 'Motivos de indeferimento', icon: '❌' },
          { id: 'essay-elimination-reasons', label: 'Motivos redação', icon: '📝' },
          { id: 'tiebreakers', label: 'Critérios de desempate', icon: '🔀' },
          { id: 'special-attendance-types', label: 'Tipos atendimento especial', icon: '♿' },
          { id: 'config', label: 'Instituição', icon: '🏛️' },
          { id: 'users', label: 'Usuários', icon: '👥' },
        ],
      },
      { id: 'analyst-exemptions', label: 'Análise de Isenções', icon: '🎫', roles: ['analyst'], onlyWithFee: true },
      { id: 'analyst-special-attendance', label: 'Atendimento especial', icon: '♿', roles: ['analyst'], onlyProva: true },
      { id: 'exam-objective', label: 'Prova objetiva', icon: '📄', roles: ['analyst'], onlyProvaWithAnswerSheet: true },
      { id: 'exam-essay', label: 'Redação', icon: '✍️', roles: ['analyst'], onlyProvaWithEssay: true },
      { id: 'analyst-applications', label: 'Análise de Inscrições', icon: '📝', roles: ['analyst'], hideForProva: true },
      { id: 'analyst-resources', label: 'Análise de Recursos', icon: '📋', roles: ['analyst'] },
    ];
    return links.filter((link) => {
      if (!link.roles.includes(userRole)) return false;
      if (link.onlyProva && !isProvaProcess) return false;
      if (link.onlyProvaWithAnswerSheet && !isProvaWithAnswerSheet) return false;
      if (link.onlyProvaWithEssay && !isProvaWithEssay) return false;
      if (link.hideForProva && isProvaProcess) return false;
      if (link.onlyWithFee && !processHasFee) return false;
      return true;
    });
  }, [userRole, isProvaProcess, isOrdemInscricaoProcess, isProvaWithAnswerSheet, isProvaWithEssay, processHasFee]);

  const isConfigActive = [
    'processes', 'courses', 'course-types', 'quotas', 'payment_methods', 'questionnaires',
    'reasons', 'essay-elimination-reasons', 'tiebreakers', 'config', 'users', 'institutions',
    'special-attendance-types',
  ].includes(currentPage);

  const renderNavButton = (link) => (
    <button
      key={link.id}
      type="button"
      onClick={() => handlePageChange(link.id)}
      style={currentPage === link.id ? styles.navLinkActive : styles.navLink}
      className={currentPage === link.id ? 'ingressa-nav-link-active' : 'ingressa-nav-link'}
      aria-current={currentPage === link.id ? 'page' : undefined}
    >
      {link.icon && <span style={{ marginRight: '6px' }}>{link.icon}</span>}
      {link.label}
    </button>
  );

  if (contextNavLinks.length === 0) return null;

  return (
    <nav style={styles.navBar} className="ingressa-candidate-nav">
      <div style={styles.navContainer} className="ingressa-nav-container">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap',
            width: '100%',
          }}
        >
          <div style={{ ...styles.navLinks, flex: '1 1 auto', flexWrap: 'wrap' }}>
            {contextNavLinks.map((link) => {
              if (link.submenu) {
                return (
                  <div key={`${link.id}-submenu`} style={{ position: 'relative' }}>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleConfigToggle(); }}
                      style={
                        isConfigActive
                          ? { ...styles.navLinkActive, display: 'flex', alignItems: 'center' }
                          : { ...styles.navLink, display: 'flex', alignItems: 'center' }
                      }
                      aria-expanded={configMenuOpen}
                    >
                      {link.icon && <span style={{ marginRight: '6px' }}>{link.icon}</span>}
                      {link.label}
                      <span style={{ marginLeft: '4px', fontSize: '10px' }}>▼</span>
                    </button>
                    {configMenuOpen && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          marginTop: '4px',
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          border: '1px solid #E5E7EB',
                          minWidth: '220px',
                          maxHeight: 'min(420px, calc(100vh - 140px))',
                          overflowY: 'auto',
                          zIndex: 100,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {link.submenu.map((sub) => (
                          <button
                            key={sub.id}
                            type="button"
                            onClick={() => handlePageChange(sub.id)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              width: '100%',
                              padding: '10px 16px',
                              border: 'none',
                              background: currentPage === sub.id ? '#EFF6FF' : 'transparent',
                              color: currentPage === sub.id ? '#1D4ED8' : '#374151',
                              cursor: 'pointer',
                              fontSize: '14px',
                              textAlign: 'left',
                            }}
                          >
                            {sub.icon && <span style={{ marginRight: '8px' }}>{sub.icon}</span>}
                            {sub.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return renderNavButton(link);
            })}
          </div>
        </div>
      </div>
    </nav>
  );
});
